import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject, PLATFORM_ID, TransferState, makeStateKey } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { of, tap } from 'rxjs';

// Bridges SSR-fetched GET /api responses to the browser via TransferState so the
// client does NOT re-fetch them on hydration.
//
// Why this is needed: company data is fetched during SSR (through the BFF on
// loopback — see ssrApiInterceptor) and serialized into <script id="ng-state">.
// Angular's built-in HTTP transfer cache keys responses by full URL, but the SSR
// request URL is the rewritten loopback URL (http://127.0.0.1:PORT/api/...) while
// the browser requests the relative /api/... — the keys never match, so the
// browser refetches. That refetch hits /api, which robots.txt disallows for
// Googlebot, so the XHR is blocked, getCompanyDetail() errors, and the page flips
// to the "Firma nu a fost gasita" state that Google then indexes.
//
// This interceptor runs BEFORE ssrApiInterceptor, so it keys on the ORIGINAL
// relative urlWithParams — identical on server and client. The client reads the
// SSR payload from TransferState and never issues the (blocked) XHR. The built-in
// HTTP transfer cache is disabled (withNoHttpTransferCache) so responses aren't
// stored twice in ng-state.
export const apiTransferStateInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.method !== 'GET' || !req.url.startsWith('/api')) {
    return next(req);
  }

  const transferState = inject(TransferState);
  const onServer = isPlatformServer(inject(PLATFORM_ID));
  const key = makeStateKey<unknown>(`api:${req.urlWithParams}`);

  if (!onServer && transferState.hasKey(key)) {
    const body = transferState.get<unknown>(key, null);
    transferState.remove(key); // one-shot: later client navigations fetch fresh
    return of(new HttpResponse({ body, status: 200, url: req.url }));
  }

  return next(req).pipe(
    tap(event => {
      if (onServer && event instanceof HttpResponse) {
        transferState.set(key, event.body);
      }
    }),
  );
};
